import { BadgeVariant } from '@arm-web/ui-components';

/**
 * Contract Type Constants
 * Static contract type options used across the application
 */

export enum ContractTypeEnum {
  DEDICATED_TEAM = 'dedicated_team',
  LUMP_SUM = 'lump_sum',
  SUPPORT = 'support',
  GENERAL_AGREEMENT = 'general_agreement',
}

export const CONTRACT_TYPE_OPTIONS = [
  { value: ContractTypeEnum.DEDICATED_TEAM, label: 'Dedicated Team' },
  { value: ContractTypeEnum.LUMP_SUM, label: 'Lump Sum' },
  { value: ContractTypeEnum.SUPPORT, label: 'Support' },
  { value: ContractTypeEnum.GENERAL_AGREEMENT, label: 'General Agreement' },
];

/**
 * Contract Status Constants
 * 8-state lifecycle managed via spatie/laravel-model-states.
 *
 * `Delayed` and `At Risk` are written by the backend automation for Lump Sum
 * contracts. This list is the *displayable* one — every value a table, badge or
 * filter may need to render. Use `manuallySelectableContractStatuses()` for the
 * shorter list a form may offer.
 */
export const CONTRACT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: '#22c55e' },
  { value: 'delayed', label: 'Delayed', color: '#ef4444' },
  { value: 'at_risk', label: 'At Risk', color: '#f97316' },
  { value: 'paused', label: 'Paused', color: '#f59e0b' },
  { value: 'completed', label: 'Completed', color: '#3b82f6' },
  { value: 'cancelled', label: 'Cancelled', color: '#9ca3af' },
  { value: 'closed', label: 'Closed', color: '#737373' },
  { value: 'expired', label: 'Expired', color: '#9ca3af' },
];

/**
 * Contract statuses the backend derives on its own. They are never offered in a
 * form, and a contract sitting on one of them has a read-only status field.
 */
export const AUTOMATIC_CONTRACT_STATUSES = ['delayed', 'at_risk'];

/**
 * The statuses a user may pick for a contract of the given type.
 *
 * At Risk is never selectable. Delayed is automatic for Lump Sum contracts only;
 * the other contract types keep it selectable.
 */
export function manuallySelectableContractStatuses(
  type: string | null | undefined,
) {
  const blocked =
    type === ContractTypeEnum.LUMP_SUM
      ? AUTOMATIC_CONTRACT_STATUSES
      : ['at_risk'];

  return CONTRACT_STATUS_OPTIONS.filter(
    (option) => !blocked.includes(option.value),
  );
}

export const CONTRACT_SERVICE_OPTIONS = [
  { value: 'software_solutions', label: 'Software Solutions' },
  { value: 'website_development', label: 'Website Development' },
  { value: 'branding', label: 'Branding' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'other_services', label: 'Other Services' },
];

/**
 * Payment Terms Options
 * Payment frequency options for Support and Dedicated Team contracts
 */
export const PAYMENT_TERMS_OPTIONS = [
  { value: 'annually', label: 'Annually' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'one_time', label: 'One-Time' },
];

/**
 * Stage Status Constants
 * 6-state lifecycle for stages within Lump Sum contracts.
 * Managed via spatie/laravel-model-states (ContractStageState).
 * Every stage status is manually selectable.
 */
export const STAGE_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started', color: '#9ca3af' },
  { value: 'in_progress', label: 'In Progress', color: '#3b82f6' },
  { value: 'delayed', label: 'Delayed', color: '#eab308' },
  { value: 'completed', label: 'Completed', color: '#22c55e' },
  { value: 'cancelled', label: 'Cancelled', color: '#ef4444' },
  { value: 'closed', label: 'Closed', color: '#737373' },
];

/**
 * The API returns a colour name per status, but Cancelled, Closed and Expired all
 * come back Gray. Keying the override off the status value keeps Closed visually
 * distinct without changing the backend colour.
 */
const CONTRACT_STATUS_BADGE_VARIANTS: Record<string, BadgeVariant> = {
  closed: 'secondary',
};

const STATUS_COLOR_BADGE_VARIANTS: Record<string, BadgeVariant> = {
  green: 'success',
  orange: 'warning',
  yellow: 'warning',
  blue: 'info',
  red: 'error',
  purple: 'primary',
  gray: 'deep',
};

/**
 * Badge variant for a contract or stage status, preferring a value-keyed override
 * over the colour the API sent.
 */
export function contractStatusBadgeVariant(
  colorName: string | undefined,
  statusValue?: string,
): BadgeVariant {
  return (
    CONTRACT_STATUS_BADGE_VARIANTS[statusValue || ''] ||
    STATUS_COLOR_BADGE_VARIANTS[colorName || ''] ||
    'primary'
  );
}
